import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, User } from '../api/client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (id: string, password: string) => Promise<{ success: boolean; message?: string; user?: User }>;
  register: (id: string, password: string, name: string, inviteCode: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.me();
      if (response.data.success && response.data.data?.user) {
        setUser(response.data.data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (id: string, password: string): Promise<{ success: boolean; message?: string; user?: User }> => {
    try {
      const response = await authApi.login(id, password);
      if (response.data.success && response.data.data?.user) {
        const loggedInUser = response.data.data.user;
        setUser(loggedInUser);
        return { success: true, user: loggedInUser };
      }
      return { success: false, message: response.data.message };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return {
        success: false,
        message: err.response?.data?.message || '로그인에 실패했습니다.',
      };
    }
  };

  const register = async (
    id: string,
    password: string,
    name: string,
    inviteCode: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authApi.register(id, password, name, inviteCode);
      if (response.data.success && response.data.data?.user) {
        setUser(response.data.data.user);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return {
        success: false,
        message: err.response?.data?.message || '회원가입에 실패했습니다.',
      };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
